import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Login from "./auth/login";
import MarketingLayout from "./marketing/MarketingLayout";
import Dashboard from "./components/Dashboard";
import ForgotPasswordPage from "./auth/ForgotPasswordPage";
import SignupPage from "./auth/SignUpPage";
import FarmersDashboard from "./pages/farmer/FarmersDashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCompanies from "./pages/admin/AdminCompanies";
import AdminCompaniesPayments from "./pages/admin/AdminCompaniesPayments";
import AdminFarmers from "./pages/admin/AdminFarmers";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminCompanyMessages from "./pages/admin/AdminCompanyMessages";
import AdminMessages from "./pages/admin/AdminMessages";
import AdminB2CTransactions from "./pages/admin/AdminB2CTransactions";
import AdminAccounting from "./pages/admin/AdminAccounting";
import EmployeeDashboard from "./pages/employee/EmployeeDashboard";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientLayoutPreview from "./pages/client/ClientLayoutPreview";
import ClientOrders from "./pages/client/ClientOrders";
import ClientInvoices from "./pages/client/ClientInvoices";
import ClientPayments from "./pages/client/ClientPayments";
import ClientSettings from "./pages/client/ClientSettings";
import CompanyDashboard from "./pages/company/CompanyDashboard";
import CompanyFarmers from "./pages/company/CompanyFarmers";
import CompanyEmployees from "./pages/company/CompanyEmployees";
import CompanyClients from "./pages/company/CompanyClients";
import CompanyPayments from "./pages/company/CompanyPayments";
import CompanyReports from "./pages/company/CompanyReports";
import CompanyMarketplace from "./pages/company/CompanyMarketplace";
import MilkJournals from "./pages/company/MilkJournals";
import PendingRequests from "./pages/company/components/PendingRequests";
import CompanySettings from "./pages/company/CompanySettings";
import ProtectedRoute from "./components/ProtectedRoute";
import FarmerMilkRecords from "./pages/farmer/FarmerMilkRecords";
import FarmerPayments from "./pages/farmer/FarmerPayments";
import FarmerAI from "./pages/farmer/FarmerAI";
import FarmerSettings from "./pages/farmer/FarmerSettings";
import AnnotationMarketplace from "./pages/common/AnnotationMarketplace";
import AccessRestrictedPage from "./pages/common/AccessRestrictedPage";
import FuelCrisisPage from "./pages/common/FuelCrisisPage";
import NeuraHelperWidget from "./components/NeuraHelper/NeuraHelperWidget";
import lazyWithRetry from "./marketing/lazyWithRetry";
import RouteBoundary from "./marketing/RouteBoundary";

const Home = lazyWithRetry(() => import("./marketing/pages/Home"));
const DairyPage = lazyWithRetry(() => import("./marketing/pages/DairyPage"));
const DataAnalyticsAI = lazyWithRetry(() => import("./marketing/pages/DataAnalyticsAI"));
const IndustrialIntelligence = lazyWithRetry(() => import("./marketing/pages/IndustrialIntelligence"));
const AgriculturalTechnology = lazyWithRetry(() => import("./marketing/pages/AgriculturalTechnology"));
const CustomSoftware = lazyWithRetry(() => import("./marketing/pages/CustomSoftware"));
const About = lazyWithRetry(() => import("./marketing/pages/About"));
const Contact = lazyWithRetry(() => import("./marketing/pages/Contact"));



function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<RouteBoundary><Home /></RouteBoundary>} />
            <Route path="/services/data-analytics-ai" element={<RouteBoundary><DataAnalyticsAI /></RouteBoundary>} />
            <Route path="/services/industrial-intelligence" element={<RouteBoundary><IndustrialIntelligence /></RouteBoundary>} />
            <Route path="/services/agricultural-technology" element={<RouteBoundary><AgriculturalTechnology /></RouteBoundary>} />
            <Route path="/services/custom-software" element={<RouteBoundary><CustomSoftware /></RouteBoundary>} />
            <Route path="/about" element={<RouteBoundary><About /></RouteBoundary>} />
            <Route path="/contact" element={<RouteBoundary><Contact /></RouteBoundary>} />
          </Route>
          <Route path="/dairy" element={<Navigate replace to="/solutions/dairy" />} />
          <Route path="/solutions/dairy" element={<RouteBoundary><DairyPage /></RouteBoundary>} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup-page" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/access-restricted" element={<AccessRestrictedPage />} />
          <Route path="/fuel-crisis" element={<FuelCrisisPage />} />
          
          {/* Client Layout Preview (for development/testing) */}
          <Route path="/client-preview" element={<ClientLayoutPreview />} />

          {/* Protected routes with role-based access */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={["Admin", "Company"]}>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/farmer-dashboard"
            element={
              <ProtectedRoute allowedRoles={["Farmer"]}>
                <FarmersDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute allowedRoles={["Admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/companies"
            element={
              <ProtectedRoute allowedRoles={["Admin"]}>
                <AdminCompanies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies/payments"
            element={
              <ProtectedRoute allowedRoles={["Admin"]}>
                <AdminCompaniesPayments />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/farmers"
            element={
              <ProtectedRoute allowedRoles={["Admin"]}>
                <AdminFarmers />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/customers"
            element={
              <ProtectedRoute allowedRoles={["Admin"]}>
                <AdminCustomers />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/company-messages"
            element={
              <ProtectedRoute allowedRoles={["Admin"]}>
                <AdminCompanyMessages />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/b2c-transactions"
            element={
              <ProtectedRoute allowedRoles={["Admin"]}>
                <AdminB2CTransactions />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/messages"
            element={
              <ProtectedRoute allowedRoles={["Admin"]}>
                <AdminMessages />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/accounting"
            element={
              <ProtectedRoute allowedRoles={["Admin"]}>
                <AdminAccounting />
              </ProtectedRoute>
            }
          />

          <Route
            path="/employee-dashboard"
            element={
              <ProtectedRoute allowedRoles={["Employee", "Admin"]}>
                <EmployeeDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/client-dashboard"
            element={
              <ProtectedRoute allowedRoles={["Client", "Company"]}>
                <ClientDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/client/orders"
            element={
              <ProtectedRoute allowedRoles={["Client", "Company"]}>
                <ClientOrders />
              </ProtectedRoute>
            }
          />

          <Route
            path="/client/invoices"
            element={
              <ProtectedRoute allowedRoles={["Client", "Company"]}>
                <ClientInvoices />
              </ProtectedRoute>
            }
          />

          <Route
            path="/client/payments"
            element={
              <ProtectedRoute allowedRoles={["Client", "Company"]}>
                <ClientPayments />
              </ProtectedRoute>
            }
          />

          <Route
            path="/client/settings"
            element={
              <ProtectedRoute allowedRoles={["Client", "Company"]}>
                <ClientSettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company-dashboard"
            element={
              <ProtectedRoute allowedRoles={["Company", "Employee"]}>
                <CompanyDashboard />
              </ProtectedRoute>
            }
          />


          <Route
            path="/company/farmers"
            element={
              <ProtectedRoute allowedRoles={["Company", "Employee"]}>
                <CompanyFarmers />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/employees"
            element={
              <ProtectedRoute allowedRoles={["Company"]}>
                <CompanyEmployees />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/clients"
            element={
              <ProtectedRoute allowedRoles={["Company", "Employee"]}>
                <CompanyClients />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/payments"
            element={
              <ProtectedRoute allowedRoles={["Company", "Employee"]}>
            <CompanyPayments />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/reports"
            element={
              <ProtectedRoute allowedRoles={["Company", "Employee"]}>
                <CompanyReports />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/settings"
            element={
              <ProtectedRoute allowedRoles={["Company"]}>
                <CompanySettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/marketplace"
            element={
              <ProtectedRoute allowedRoles={["Company", "Employee"]}>
                <CompanyMarketplace />
              </ProtectedRoute>
            }
          />

          <Route
            path="/company/milk-journals"
            element={
              <ProtectedRoute allowedRoles={["Company", "Employee"]}>
                <MilkJournals />
              </ProtectedRoute>
            }
          />



          <Route
            path="/company/pending-requests"
            element={
              <ProtectedRoute allowedRoles={["Company"]}>
                <PendingRequests />
              </ProtectedRoute>
            }
          />


          <Route
            path="/farmer/milk-records"
            element={
              <ProtectedRoute allowedRoles={["Farmer"]}>
                <FarmerMilkRecords />
              </ProtectedRoute>
            }
          />

          <Route
            path="/farmer/payments"
            element={
              <ProtectedRoute allowedRoles={["Farmer"]}>
                <FarmerPayments />
              </ProtectedRoute>
            }
          />

          <Route
            path="/farmer/ai-assistant"
            element={
              <ProtectedRoute allowedRoles={["Farmer"]}>
                <FarmerAI />
              </ProtectedRoute>
            }
          />

          <Route
            path="/farmer/settings"
            element={
              <ProtectedRoute allowedRoles={["Farmer"]}>
                <FarmerSettings />
              </ProtectedRoute>
            }
          />

          {/* Annotation Marketplace - accessible to Farmer, Client, and Company */}
          <Route
            path="/annotation-marketplace"
            element={
              <ProtectedRoute allowedRoles={["Farmer", "Client", "Company", "Employee"]}>
                <AnnotationMarketplace />
              </ProtectedRoute>
            }
          />

          {/* Optional Unauthorized page */}
          <Route
            path="/unauthorized"
            element={
              <div className="flex flex-col items-center justify-center h-screen">
                <h1 className="text-3xl font-bold text-red-600">
                  Access Denied
                </h1>
                <p className="mt-2 text-gray-600">
                  You don’t have permission to view this page.
                </p>
              </div>
            }
          />
        </Routes>
        {/* NeuraHelper AI Agent – floating widget available on all pages */}
        <NeuraHelperWidget />
      </div>
    </Router>
  );
}

export default App;
